import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Table, Button, Form, Modal, Spinner } from "react-bootstrap";
import authService from '../../../utils/authService';
import PulseLoader from "@/components/PulseLoader";
import { countSubjectPlanItems } from "./confirmSubjectPlanCounts";

interface Item {
  itemType: 'video' | 'file' | 'notebook' | 'marimo';
  content_id: number;
  time: string;
}

interface Subtopic {
  subtopicName: string;
  mcq: { [key: string]: number };
  coding: { [key: string]: number };
  optionalMcq: { [key: string]: number };
  optionalCoding: { [key: string]: number };
  items: Item[];
}

interface Topic {
  topicName: string;
  subtopics: { [key: string]: Subtopic };
}

interface SelectedItems {
  subject_id: string;
  subject_name: string;
  course: { course_id: string };
  topics: { id: string; name: string; subtopics: any[] }[];
}

function ReorderContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const [time, setTime] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [items, setItems] = useState<{ [key: string]: Topic }>({});
  const [modalNewMessage, setModalNewMessage] = useState<string>("");
  const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false);
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>("");

  useEffect(() => {
    if (!location.state?.selectedItems) {
      navigate('/');
      return;
    }

    const selectedItems = location.state?.selectedItems as SelectedItems;
    setTime(location.state.time);

    const groupedItems: { [key: string]: Topic } = {};

    selectedItems.topics.forEach((topic: { id: string; name: string; subtopics: any[] }) => {
      groupedItems[topic.id] = {
        topicName: topic.name,
        subtopics: {}
      };

      topic.subtopics.forEach((subtopic: { id: string; subtopicName: string; mcq: { [key: string]: number }; coding: { [key: string]: number }; optionalMcq: { [key: string]: number }; optionalCoding: { [key: string]: number }; data: any[] }) => {
        groupedItems[topic.id].subtopics[subtopic.id] = {
          subtopicName: subtopic.subtopicName,
          mcq: subtopic.mcq || {},
          coding: subtopic.coding || {},
          optionalMcq: subtopic.optionalMcq || {},
          optionalCoding: subtopic.optionalCoding || {},
          items: subtopic.data.map((item: any) => ({
            itemType: item.type as 'video' | 'file' | 'notebook' | 'marimo',
            content_id: item.content_id,
            time: item.time,
          }))
        };
      });
    });

    setItems(groupedItems);
  }, [location.state, navigate]);

  const handleSave = async () => {
    setIsLoading(true);
    const sub_id = location.state?.selectedItems.subject_id;
    const liveSessions = Array.isArray(location.state?.liveSessions)
      ? location.state.liveSessions
      : [];
    try {
      const orderedData = {
        subject_data: {
          [sub_id]: {
            subject_name: location.state?.selectedItems.subject_name,
            topics: Object.keys(items).map((topicId) => ({
              id: topicId,
              name: items[topicId].topicName,
              subtopics: Object.keys(items[topicId].subtopics).map((subtopicId) => ({
                id: subtopicId,
                subtopicName: items[topicId].subtopics[subtopicId].subtopicName,
                mcq: items[topicId].subtopics[subtopicId].mcq,
                coding: items[topicId].subtopics[subtopicId].coding,
                optionalMcq: items[topicId].subtopics[subtopicId].optionalMcq,
                optionalCoding: items[topicId].subtopics[subtopicId].optionalCoding,
                data: items[topicId].subtopics[subtopicId].items.map((item) => ({
                  type: item.itemType,
                  content_id: item.content_id,
                  time: item.time,
                }))
              }))
            }))
          }
        },
        subject_id: sub_id,
        course_id: location.state?.selectedItems.course.course_id,
        created_by: sessionStorage.getItem('Email'),
        time: time,
        // Planned LCM / workshop drafts → Mongo course_live_session_plans
        live_sessions: liveSessions,
      };
      const response = await authService.post('admin/content/save_subject_plans_details/', orderedData);

      if (response.status === 200) {
        const plannedCount =
          typeof response.data?.planned_session_count === 'number'
            ? response.data.planned_session_count
            : liveSessions.length;
        const plannedNote =
          plannedCount > 0
            ? ` ${plannedCount} live session draft(s) saved as planned.`
            : '';
        setSuccessMessage(
          `Course Configuration done successfully!${plannedNote} Add or Update the batch calendar for changes made in course configuration, otherwise changes will not reflect in student side.`,
        );
        setShowSuccessModal(true);
      } else {
        setModalNewMessage('Error during course config');
        setShowUpdateModal(true);
      }
    } catch (error) {
      console.error('Error saving order:', error);
      setModalNewMessage('Error saving the order. Please try again.');
      setShowUpdateModal(true);
    }
    setIsLoading(false);
  };

  const calculateTotalTime = () => {
    // Use the time passed from the previous page instead of calculating from items
    return time;
  };

  const getTotalItems = () => {
    let totalItems = 0;
    Object.keys(items).forEach(topicId => {
      Object.keys(items[topicId].subtopics).forEach(subtopicId => {
        totalItems += items[topicId].subtopics[subtopicId].items.length;
      });
    });
    return totalItems;
  };

  const getTotalSubtopics = () => {
    let totalSubtopics = 0;
    Object.keys(items).forEach(topicId => {
      totalSubtopics += Object.keys(items[topicId].subtopics).length;
    });
    return totalSubtopics;
  };

  const getItemCounts = () =>
    countSubjectPlanItems(items, location.state?.selectedItems?.topics || []);

  const counts = getItemCounts();
  const liveSessionsList = Array.isArray(location.state?.liveSessions)
    ? location.state.liveSessions
    : [];
  const liveSessionCount =
    typeof location.state?.liveSessionCount === 'number'
      ? location.state.liveSessionCount
      : liveSessionsList.filter(
          (s: { category?: string }) => s?.category === 'mandatory',
        ).length;
  const workshopCount =
    typeof location.state?.workshopCount === 'number'
      ? location.state.workshopCount
      : liveSessionsList.filter((s: { category?: string }) =>
          String(s?.category || '').startsWith('workshop'),
        ).length;

  const goBackToLiveSessions = () => {
    navigate('/admin/course-live-sessions', {
      state: {
        selectedItems: location.state?.selectedItems,
        subjectPlan: location.state?.subjectPlan,
        time: location.state?.time,
        liveSessions: liveSessionsList,
      },
    });
  };

  return (
    <div className="border pt-3 px-2 rounded-2 bg-white my-2 me-2"
      style={{ height: `calc(100vh - 90px)`, overflowY: "auto" }}>
      <div style={{ width: `calc(100% - 5px)` }}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="mb-1">Confirm Subject Plan</h4>
          <p className="text-muted mb-0">
            Review your selected content before saving
          </p>
        </div>
        <div>
          <button
            className="btn btn-secondary me-2"
            onClick={goBackToLiveSessions}
          >
            Back
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={Object.keys(items).length === 0}
          >
            Save Plan
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="row mb-4 g-2">
        <div className="col">
          <div className="card bg-primary text-white">
            <div className="card-body text-center">
              <h6 className="card-title">Topics</h6>
              <h4>{Object.keys(items).length}</h4>
            </div>
          </div>
        </div>
        <div className="col">
          <div className="card bg-success text-white">
            <div className="card-body text-center">
              <h6 className="card-title">Subtopics</h6>
              <h4>{getTotalSubtopics()}</h4>
            </div>
          </div>
        </div>
        <div className="col">
          <div className="card bg-info text-white">
            <div className="card-body text-center">
              <h6 className="card-title">Videos</h6>
              <h4>{counts.videos}</h4>
            </div>
          </div>
        </div>
        <div className="col">
          <div className="card bg-warning text-white">
            <div className="card-body text-center">
              <h6 className="card-title">Notes</h6>
              <h4>{counts.notes}</h4>
            </div>
          </div>
        </div>
        <div className="col">
          <div className="card text-white" style={{ backgroundColor: '#20c997' }}>
            <div className="card-body text-center">
              <h6 className="card-title">Notebooks</h6>
              <h4>{counts.notebooks}</h4>
            </div>
          </div>
        </div>
        <div className="col">
          <div className="card bg-danger text-white">
            <div className="card-body text-center">
              <h6 className="card-title">MCQs</h6>
              <h4>{counts.mcqs}</h4>
            </div>
          </div>
        </div>
        <div className="col">
          <div className="card bg-secondary text-white">
            <div className="card-body text-center">
              <h6 className="card-title">Coding</h6>
              <h4>{counts.coding}</h4>
            </div>
          </div>
        </div>
      </div>

      {/* Live Sessions & Workshops (from intermediate course plan step) */}
      <div className="row mb-4">
        <div className="col-md-6">
          <div className="card text-white" style={{ backgroundColor: '#0d6efd' }}>
            <div className="card-body text-center">
              <h6 className="card-title">Live Sessions</h6>
              <h4>{liveSessionCount}</h4>
              <small className="opacity-75">Live Class Mandatory (LCM)</small>
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="card text-white" style={{ backgroundColor: '#6610f2' }}>
            <div className="card-body text-center">
              <h6 className="card-title">Workshops</h6>
              <h4>{workshopCount}</h4>
              <small className="opacity-75">Online / Onsite</small>
            </div>
          </div>
        </div>
      </div>

      {/* Optional Content Cards */}
      <div className="row mb-4">
        <div className="col-md-6">
          <div className="card bg-light border-warning">
            <div className="card-body text-center">
              <h6 className="card-title text-warning">Optional MCQs</h6>
              <h4 className="text-warning">{counts.optionalMcqs}</h4>
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="card bg-light border-warning">
            <div className="card-body text-center">
              <h6 className="card-title text-warning">Optional Coding</h6>
              <h4 className="text-warning">{counts.optionalCoding}</h4>
            </div>
          </div>
        </div>
      </div>

      {/* Total Time Card */}
      <div className="row mb-4">
        <div className="col-md-12">
          <div className="card bg-dark text-white">
            <div className="card-body text-center">
              <h5 className="card-title">Total Time</h5>
              <h3>{Math.floor(calculateTotalTime() / 60)}h {(calculateTotalTime() % 60)}m</h3>
            </div>
          </div>
        </div>
      </div>


      {isLoading && (
        <div className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-white bg-opacity-75" style={{ zIndex: 1000 }}>
          <PulseLoader size={10} />
        </div>
      )}

      <Modal show={showUpdateModal} onHide={() => setShowUpdateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Message</Modal.Title>
        </Modal.Header>
        <Modal.Body>{modalNewMessage}</Modal.Body>
      </Modal>

      <Modal show={showSuccessModal} onHide={() => {}} size="lg" backdrop="static" keyboard={false}>
        <Modal.Header>
          <Modal.Title className="text-white">
            Course Configuration Success
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="alert alert-success m-0" role="alert">
            <h6 className="alert-heading">
              Important Notice
            </h6>
            <p className="mb-0">{successMessage}</p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => navigate('/admin/day-wise-card')}>
            Go to Batch Calendar
          </Button>
        </Modal.Footer>
      </Modal>
      </div>
    </div>
  );
}

export default ReorderContent;
